import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

interface HealthStatusCardProps {
  title: string;
  status: 'healthy' | 'warning' | 'error';
  loading?: boolean;
  latency?: number;
  message?: string;
}

const statusConfig = {
  healthy: {
    icon: CheckCircle,
    bgcolor: 'success.lighter',
    color: 'success.dark',
    borderColor: 'success.light',
  },
  warning: {
    icon: AlertTriangle,
    bgcolor: 'warning.lighter',
    color: 'warning.dark',
    borderColor: 'warning.light',
  },
  error: {
    icon: XCircle,
    bgcolor: 'error.lighter',
    color: 'error.dark',
    borderColor: 'error.light',
  },
};

export function HealthStatusCard({ title, status, loading, latency, message }: HealthStatusCardProps) {
  if (loading) {
    return (
      <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Skeleton variant="circular" width={32} height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={80} />
      </Card>
    );
  }

  const config = statusConfig[status];
  const Icon = config.icon;

  const cardContent = (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: config.bgcolor,
        borderColor: config.borderColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ color: config.color }}>
          <Icon size={24} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.75, color: config.color }}>
              {title}
            </Typography>
            {latency !== undefined && (
              <Typography variant="caption" sx={{ opacity: 0.6, color: config.color, whiteSpace: 'nowrap' }}>
                {latency}ms
              </Typography>
            )}
          </Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: config.color, textTransform: 'capitalize' }}>
            {status}
          </Typography>
        </Box>
      </Box>
    </Card>
  );

  // Wrap in tooltip if there's a message
  if (message) {
    return (
      <Tooltip title={message} arrow placement="top">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
}
